/**
 * ChatApp - Main LLM Proxy Chat Application
 * Interactive TUI for chatting with LLM API
 * Simple layout to avoid re-render flickering
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Text, useInput, useApp, useStdout } from 'ink';
import Spinner from 'ink-spinner';
import { LlmProxyService } from '../../services/llm-proxy.service.js';
import { useChat } from './hooks/useChat.js';
import { useLlmApi } from './hooks/useLlmApi.js';
import { ChatPanel } from './components/ChatPanel.js';
import { ModelSelector } from './components/ModelSelector.js';
import { SlashCommand } from './components/SlashCommandMenu.js';

// Available slash commands - defined outside component to prevent recreation
const SLASH_COMMANDS: SlashCommand[] = [
    { name: 'model', description: 'Change the AI model', aliases: ['m'] },
    { name: 'stats', description: 'Show model usage statistics', aliases: ['s'] },
    { name: 'quit', description: 'Exit the chat', aliases: ['q', 'exit'] },
];

interface ChatAppProps {
    service: LlmProxyService;
    initialModel?: string;
}

type ViewType = 'loading' | 'chat' | 'model' | 'stats' | 'error';

export const ChatApp: React.FC<ChatAppProps> = ({ service, initialModel }) => {
    const { exit } = useApp();
    const { stdout } = useStdout();

    // Capture terminal inner height once on mount (subtract header + footer = ~6 lines)
    const contentHeight = useRef(Math.max(10, (stdout?.rows || 24) - 6));

    // Core state
    const [currentView, setCurrentView] = useState<ViewType>('loading');
    const [showSlashMenu, setShowSlashMenu] = useState(false);
    const [slashMenuIndex, setSlashMenuIndex] = useState(0);
    const [inputValue, setInputValue] = useState('');
    const [selectedModel, setSelectedModel] = useState<string>('');

    // API data
    const { models, loading, error, refetch } = useLlmApi(service);

    // Chat state
    const { messages, isStreaming, sendMessage } = useChat(service);

    // Set initial model when loaded, or show error
    useEffect(() => {
        if (error && !loading) {
            setCurrentView('error');
            return;
        }
        if (models.length > 0 && !selectedModel) {
            const first = models.find((m) => m.allowed);
            if (first) {
                setSelectedModel(initialModel || first.id);
                setCurrentView('chat');
            }
        }
    }, [models, selectedModel, initialModel, error, loading]);

    // Filter commands based on input
    const filteredCommands = useMemo(() => {
        if (!inputValue.startsWith('/')) return SLASH_COMMANDS;
        const filter = inputValue.slice(1).toLowerCase();
        return SLASH_COMMANDS.filter(
            (cmd) => cmd.name.includes(filter) || cmd.aliases?.some((a) => a.includes(filter))
        );
    }, [inputValue]);

    // Handlers
    const cycleModel = useCallback(() => {
        const allowed = models.filter((m) => m.allowed);
        if (allowed.length === 0) return;
        const idx = allowed.findIndex((m) => m.id === selectedModel);
        setSelectedModel(allowed[(idx + 1) % allowed.length]!.id);
    }, [models, selectedModel]);

    const execCommand = useCallback((cmd: string) => {
        const c = cmd.toLowerCase().replace(/^\//, '');
        if (c === 'model' || c === 'm') { setCurrentView('model'); return true; }
        if (c === 'stats' || c === 's') { setCurrentView('stats'); refetch(); return true; }
        if (c === 'quit' || c === 'q' || c === 'exit') { exit(); return true; }
        return false;
    }, [refetch, exit]);

    // Keyboard input
    useInput((input, key) => {
        if (showSlashMenu) {
            if (key.upArrow) { setSlashMenuIndex((i) => Math.max(0, i - 1)); return; }
            if (key.downArrow) { setSlashMenuIndex((i) => Math.min(filteredCommands.length - 1, i + 1)); return; }
            if (key.return && filteredCommands[slashMenuIndex]) {
                setShowSlashMenu(false);
                execCommand(filteredCommands[slashMenuIndex].name);
                setInputValue('');
                return;
            }
        }
        if (key.escape) {
            if (showSlashMenu) { setShowSlashMenu(false); setInputValue(''); }
            else if (currentView !== 'chat' && currentView !== 'loading' && currentView !== 'error') { setCurrentView('chat'); }
            else { exit(); }
            return;
        }
        // Allow retry on error view with Enter or 'r'
        if (currentView === 'error' && (key.return || input === 'r')) {
            setCurrentView('loading');
            refetch();
            return;
        }
        if (key.tab && currentView === 'chat' && !showSlashMenu && !isStreaming) {
            cycleModel();
        }
    }, { isActive: true });

    const onInputChange = useCallback((v: string) => {
        setInputValue(v);
        const show = v.startsWith('/');
        setShowSlashMenu(show);
        if (show && v === '/') setSlashMenuIndex(0);
    }, []);

    const onSubmit = useCallback((v: string) => {
        const t = v.trim();
        setShowSlashMenu(false);
        if (t.startsWith('/') && execCommand(t)) { setInputValue(''); return; }
        if (t && !isStreaming && selectedModel) { sendMessage(t, selectedModel); setInputValue(''); }
    }, [execCommand, isStreaming, selectedModel, sendMessage]);

    const onSelectModel = useCallback((id: string) => {
        setSelectedModel(id);
        setCurrentView('chat');
    }, []);

    // Render stats
    const statsView = useMemo(() => {
        const allowed = models.filter((m) => m.allowed);
        return (
            <Box flexDirection="column" borderStyle="round" borderColor="cyan" padding={1}>
                <Text bold color="cyan">📊 Model Usage Statistics</Text>
                {allowed.map((m) => {
                    const limit = m.dailyLimit ?? 0;
                    const used = m.usedToday ?? 0;
                    const pct = limit > 0 ? Math.round((used / limit) * 100) : 0;
                    const bar = '█'.repeat(Math.round(pct / 5)) + '░'.repeat(20 - Math.round(pct / 5));
                    return (
                        <Box key={m.id} flexDirection="column">
                            <Text color={m.id === selectedModel ? 'yellow' : 'white'}>
                                {m.id === selectedModel ? '► ' : '  '}{m.id}
                            </Text>
                            <Text color={pct > 80 ? 'red' : pct > 50 ? 'yellow' : 'green'}>
                                {'    '}{bar} <Text dimColor>{used}/{limit}</Text>
                            </Text>
                        </Box>
                    );
                })}
                <Text dimColor>[Esc] Back</Text>
            </Box>
        );
    }, [models, selectedModel]);

    // Footer text
    const footer = currentView === 'error'
        ? '[Enter/r] Retry • [Esc] Quit'
        : currentView === 'model' || currentView === 'stats'
        ? '[↑↓] Navigate • [Enter] Select • [Esc] Back'
        : showSlashMenu
        ? '[↑↓] Navigate • [Enter] Select • [Esc] Cancel'
        : '[Tab] Next Model • [/] Commands • [Esc] Quit';

    return (
        <Box flexDirection="column">
            {/* Header */}
            <Box borderStyle="double" borderColor="magenta" paddingX={1}>
                <Text bold color="magenta">🤖 Jai1 Chat</Text>
                <Box flexGrow={1} />
                <Text dimColor>Model: </Text>
                <Text color="yellow" bold>{selectedModel || '...'}</Text>
            </Box>

            {/* Content */}
            {currentView === 'loading' ? (
                <Box padding={2} minHeight={contentHeight.current} alignItems="center" justifyContent="center">
                    <Text><Spinner type="dots" /> Loading...</Text>
                </Box>
            ) : currentView === 'error' ? (
                <Box padding={2} minHeight={contentHeight.current} alignItems="center" justifyContent="center" flexDirection="column">
                    <Text color="red" bold>Connection Error</Text>
                    <Text color="red">{error}</Text>
                    <Text dimColor> </Text>
                    <Text dimColor>Press Enter or 'r' to retry</Text>
                </Box>
            ) : currentView === 'model' ? (
                <Box padding={1} minHeight={contentHeight.current} justifyContent="center" alignItems="center">
                    <ModelSelector
                        models={models}
                        currentModel={selectedModel}
                        onSelect={onSelectModel}
                        onCancel={() => setCurrentView('chat')}
                    />
                </Box>
            ) : currentView === 'stats' ? (
                <Box padding={1} minHeight={contentHeight.current} justifyContent="center" alignItems="center">
                    {statsView}
                </Box>
            ) : (
                <ChatPanel
                    messages={messages}
                    isStreaming={isStreaming}
                    inputValue={inputValue}
                    onInputChange={onInputChange}
                    onSubmit={onSubmit}
                    inputFocused={!showSlashMenu}
                    showSlashMenu={showSlashMenu}
                    slashCommands={filteredCommands}
                    slashMenuIndex={slashMenuIndex}
                    minHeight={contentHeight.current}
                />
            )}

            {/* Footer */}
            <Box borderStyle="single" borderColor="gray" paddingX={1}>
                <Text dimColor>{footer}</Text>
            </Box>
        </Box>
    );
};
